import React, { useState } from 'react';
import { createEvent, updateEvent } from '../../utils/eventApi';
import { EVENT_CATEGORIES, toShareRequests, todayIso } from '../../utils/eventUtils';
import SplitEditor, { getRemainingAmount } from './SplitEditor';

// Creates an event in a group, optionally with its first bill and the advance already paid on it.
// With `eventToEdit` it edits the event's own details instead.
function CreateEventModal({ group, users, currentUser, eventToEdit, onClose, onSuccess }) {
  const isEditing = !!eventToEdit;
  const groupUsers = users.filter((u) => group.members.some((m) => m.userId === u.id));

  const [name, setName] = useState(isEditing ? eventToEdit.name : '');
  const [category, setCategory] = useState(isEditing ? eventToEdit.category || 'other' : 'hotel');
  const [memberIds, setMemberIds] = useState(
    isEditing ? eventToEdit.memberIds : groupUsers.map((u) => u.id)
  );
  const [expectedPayerId, setExpectedPayerId] = useState(isEditing ? eventToEdit.expectedPayerId || '' : '');
  const [status, setStatus] = useState(isEditing ? eventToEdit.status : 'OPEN');

  // First bill (create only)
  const [itemName, setItemName] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [splitNow, setSplitNow] = useState(true);
  const [splitType, setSplitType] = useState('equal');
  const [selectedParticipants, setSelectedParticipants] = useState(groupUsers.map((u) => u.id));
  const [exactAmounts, setExactAmounts] = useState({});
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advancePaidBy, setAdvancePaidBy] = useState(currentUser.id);
  const [advanceLabel, setAdvanceLabel] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const members = groupUsers.filter((u) => memberIds.includes(u.id));
  const hasFirstItem = !isEditing && parseFloat(totalAmount) > 0;
  const remainingAmount = getRemainingAmount(totalAmount, selectedParticipants, exactAmounts);
  const isSubmitDisabled =
    loading || (hasFirstItem && splitNow && splitType === 'exact' && Math.abs(remainingAmount) >= 0.01);

  const toggleMember = (userId) => {
    if (memberIds.includes(userId)) {
      setMemberIds(memberIds.filter((id) => id !== userId));
      setSelectedParticipants(selectedParticipants.filter((id) => id !== userId));
    } else {
      setMemberIds([...memberIds, userId]);
      setSelectedParticipants([...selectedParticipants, userId]);
    }
  };

  const displayName = (u) => (u.id === currentUser.id ? 'You' : u.name);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Event name is required.');
      return;
    }
    if (memberIds.length === 0) {
      setError('At least one member must be selected.');
      return;
    }

    const payload = {
      groupId: group.id,
      name: name.trim(),
      category,
      memberIds,
      expectedPayerId: expectedPayerId || null,
    };

    if (isEditing) {
      payload.status = status;
    } else if (hasFirstItem) {
      const total = parseFloat(totalAmount);
      const advance = parseFloat(advanceAmount) || 0;
      if (splitNow && selectedParticipants.length === 0) {
        setError('At least one participant must be selected.');
        return;
      }
      if (advance < 0 || advance > total) {
        setError('The advance cannot be more than the total amount.');
        return;
      }
      if (advance > 0 && !memberIds.includes(advancePaidBy)) {
        setError('Choose who paid the advance.');
        return;
      }

      const itemLabel = itemName.trim() || name.trim();
      payload.items = [
        {
          name: itemLabel,
          totalAmount: total,
          // Left empty when the split will be decided later.
          parts: splitNow
            ? [
                {
                  amount: total,
                  splitType: splitType.toUpperCase(),
                  shares: toShareRequests(splitType, selectedParticipants, exactAmounts),
                },
              ]
            : [],
          payments:
            advance > 0
              ? [
                  {
                    label: advanceLabel.trim() || `${itemLabel} advance`,
                    paidByUserId: advancePaidBy,
                    amount: advance,
                    type: advance >= total ? 'FINAL' : 'ADVANCE',
                    paidOn: todayIso(),
                  },
                ]
              : [],
        },
      ];
    }

    setLoading(true);
    try {
      if (isEditing) {
        await updateEvent(eventToEdit.id, payload);
      } else {
        await createEvent(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Error saving event.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content animate-slide-up">
        <div className="modal-header">
          <h3>{isEditing ? '✏️ Edit Event' : '🎟️ Create an Event'}</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="event-name">Event Name</label>
              <input
                id="event-name"
                type="text"
                placeholder="e.g. Hotel Stay – Goa"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-category">Category</label>
              <select id="event-category" value={category} onChange={(e) => setCategory(e.target.value)}>
                {EVENT_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group participants-scroll">
            <label>Who Is Part Of This Event</label>
            <div className="participants-checklist">
              {groupUsers.map((u) => (
                <div key={u.id} className="participant-check-row">
                  <div className="check-label" onClick={() => toggleMember(u.id)}>
                    <input type="checkbox" checked={memberIds.includes(u.id)} readOnly />
                    <span>{u.id === currentUser.id ? `${u.name} (You)` : u.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isEditing ? (
            <div className="form-row-2">
              <div className="form-group">
                <label htmlFor="event-expected-payer">Who will pay the rest? (optional)</label>
                <select
                  id="event-expected-payer"
                  value={expectedPayerId}
                  onChange={(e) => setExpectedPayerId(e.target.value)}
                >
                  <option value="">Not decided</option>
                  {members.map((u) => (
                    <option key={u.id} value={u.id}>
                      {displayName(u)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="event-status">Status</label>
                <select id="event-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="OPEN">Open</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
            </div>
          ) : (
            <>
              <div className="event-form-section">
                <span className="event-form-section-title">First bill (optional)</span>
                <span className="event-form-section-hint">
                  Enter the total and any advance already paid. More items and payments can be added later.
                </span>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor="event-item-name">Item</label>
                  <input
                    id="event-item-name"
                    type="text"
                    placeholder="e.g. Room booking"
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="event-item-total">Total Amount (₹)</label>
                  <input
                    id="event-item-total"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={totalAmount}
                    onChange={(e) => setTotalAmount(e.target.value)}
                  />
                </div>
              </div>

              {hasFirstItem && (
                <>
                  <div className="form-group">
                    <label>When To Split</label>
                    <div className="tab-buttons">
                      <button
                        type="button"
                        className={`tab-btn ${splitNow ? 'active' : ''}`}
                        onClick={() => setSplitNow(true)}
                      >
                        Split now
                      </button>
                      <button
                        type="button"
                        className={`tab-btn ${!splitNow ? 'active' : ''}`}
                        onClick={() => setSplitNow(false)}
                      >
                        Split later
                      </button>
                    </div>
                  </div>

                  {splitNow ? (
                    <SplitEditor
                      users={members}
                      currentUser={currentUser}
                      amount={totalAmount}
                      splitType={splitType}
                      onSplitTypeChange={setSplitType}
                      selectedParticipants={selectedParticipants}
                      onSelectedParticipantsChange={setSelectedParticipants}
                      exactAmounts={exactAmounts}
                      onExactAmountsChange={setExactAmounts}
                    />
                  ) : (
                    <div className="remaining-amount-banner pending">
                      <span>
                        The split can be added later, in one go or in parts. Payments are recorded now and
                        count in balances once the split is complete.
                      </span>
                    </div>
                  )}

                  <div className="form-row-2">
                    <div className="form-group">
                      <label htmlFor="event-advance-amount">Advance Paid (₹)</label>
                      <input
                        id="event-advance-amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={advanceAmount}
                        onChange={(e) => setAdvanceAmount(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="event-advance-payer">Advance Paid By</label>
                      <select
                        id="event-advance-payer"
                        value={advancePaidBy}
                        onChange={(e) => setAdvancePaidBy(e.target.value)}
                      >
                        {members.map((u) => (
                          <option key={u.id} value={u.id}>
                            {displayName(u)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {parseFloat(advanceAmount) > 0 && (
                    <div className="form-group">
                      <label htmlFor="event-advance-label">Payment Name</label>
                      <input
                        id="event-advance-label"
                        type="text"
                        maxLength={80}
                        placeholder={`${itemName.trim() || name.trim() || 'Hotel'} advance`}
                        value={advanceLabel}
                        onChange={(e) => setAdvanceLabel(e.target.value)}
                      />
                    </div>
                  )}

                  {parseFloat(advanceAmount) > 0 && parseFloat(advanceAmount) <= parseFloat(totalAmount) && (
                    <div className="remaining-amount-banner zero">
                      <span>
                        ₹{(parseFloat(totalAmount) - parseFloat(advanceAmount)).toFixed(2)} will remain to be paid
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitDisabled}>
              {loading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEventModal;
